import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["input", "output", "increment", "decrement"];

    static values = {
        min: Number,
        max: Number,
        step: { type: Number, default: 1 },
        initial: { type: Number, default: 1 },
    };

    connect() {
        const fromInput = parseInt(this.inputTarget?.value || "", 10);

        this.value = Number.isFinite(fromInput)
            ? fromInput
            : (
                this.hasInitialValue
                    ? this.initialValue
                    : (this.hasMinValue ? this.minValue : 0)
            );

        this.value = this.clamp(this.value);
        this.render();

        this.element.addEventListener("keydown", this.onKeyDown);
    }

    disconnect() {
        this.element.removeEventListener("keydown", this.onKeyDown);
    }

    onKeyDown = (event) => {
        const incrementKey =
            ["ArrowUp", "ArrowRight", "+", "="].includes(event.key) ||
            event.code === "NumpadAdd";

        const decrementKey =
            ["ArrowDown", "ArrowLeft", "-"].includes(event.key) ||
            event.code === "NumpadSubtract";

        if (incrementKey) {
            event.preventDefault();
            this.increment();
        } else if (decrementKey) {
            event.preventDefault();
            this.decrement();
        }
    };

    increment() {
        this.set(
            this.value + (this.hasStepValue ? this.stepValue : 1)
        );
    }

    decrement() {
        this.set(
            this.value - (this.hasStepValue ? this.stepValue : 1)
        );
    }

    set(value) {
        const next = this.clamp(value);

        if (next === this.value) {
            this.render();
            return;
        }

        this.value = next;
        this.render();
        this.emitChange();
    }

    emitChange() {
        // Native change event from the actual hidden input.
        // Your venue-attendees controller listens for this event.
        if (this.hasInputTarget) {
            this.inputTarget.dispatchEvent(
                new Event("change", {
                    bubbles: true,
                })
            );
        }

        // Keep the Stimulus `counter:change` event as well in case another
        // controller uses data-action="counter:change->controller#method".
        this.dispatch("change", {
            detail: {
                value: this.value,
            },
        });
    }

    clamp(value) {
        let next = value;

        if (this.hasMinValue) {
            next = Math.max(next, this.minValue);
        }

        if (this.hasMaxValue) {
            next = Math.min(next, this.maxValue);
        }

        return next;
    }

    render() {
        if (this.hasOutputTarget) {
            this.outputTarget.textContent = String(this.value);
        }

        if (this.hasInputTarget) {
            this.inputTarget.value = String(this.value);
        }

        this.element.setAttribute(
            "aria-valuenow",
            String(this.value)
        );

        const atMin =
            this.hasMinValue &&
            this.value <= this.minValue;

        const atMax =
            this.hasMaxValue &&
            this.value >= this.maxValue;

        if (this.hasDecrementTarget) {
            this.decrementTarget.toggleAttribute(
                "disabled",
                atMin
            );
        }

        if (this.hasIncrementTarget) {
            this.incrementTarget.toggleAttribute(
                "disabled",
                atMax
            );
        }
    }
}